import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
// ── Types ──────────────────────────────────────────────────────────
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string | null;
  checkoutUrl?: string;
}
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
  visible: boolean;
}
// ── Argentine Data ─────────────────────────────────────────────────
const NAMES = ['Mariela', 'Claudia', 'Silvia', 'Gabriela', 'Laura', 'Carolina', 'Valeria', 'Romina', 'Florencia', 'Luciana', 'Marcela', 'Andrea', 'Patricia', 'Verónica', 'Natalia', 'Roberto', 'Carlos', 'Martín', 'Diego', 'Fernando', 'Alejandro', 'Gustavo', 'Pablo', 'Sergio', 'Daniel', 'Hernán', 'Facundo', 'Nicolás', 'Matías', 'Lucas', 'Soledad', 'Julieta', 'Agustina', 'Camila', 'Micaela', 'Jorge', 'Ricardo', 'Raúl', 'Eduardo', 'Hugo'];
const CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'San Miguel de Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Posadas', 'Quilmes', 'Lanús', 'Bahía Blanca', 'Corrientes', 'Morón', 'San Isidro', 'Avellaneda', 'Lomas de Zamora', 'Paraná', 'Formosa', 'San Luis', 'Santiago del Estero', 'Río Cuarto', 'Concordia', 'San Rafael', 'Tandil', 'Junín', 'Pergamino', 'Olavarría', 'Rafaela', 'Villa María', 'San Nicolás', 'Zárate', 'Campana', 'Pilar'];
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'Botiquín Ancestral',
  editionLabel: 'Pack Completo + Bonos',
  priceNumber: 17990,
  priceText: '$17.990',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/t8MbsHq6sN5tfKStYECKy6/EBOOK_PRINCIPAL.png',
  checkoutUrl: 'https://ebookpromax.com/cart/add?id=46629742641310&quantity=1'
}, {
  title: 'Botiquín Ancestral',
  editionLabel: 'Pack Completo + Order Bump',
  priceNumber: 25490,
  priceText: '$25.490',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/3A3YCYFLBHQd2WKN5WLqGo/BUNDLE_1.png',
  checkoutUrl: 'https://ebookpromax.com/cart/add?id=46629774295198&quantity=1'
}, {
  title: 'Botiquín Ancestral',
  editionLabel: 'Pack Especial',
  priceNumber: 23240,
  priceText: '$23.240',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/3A3YCYFLBHQd2WKN5WLqGo/BUNDLE_1.png',
  checkoutUrl: 'https://ebookpromax.com/cart/add?id=46629774753950&quantity=1'
}];
// ── Product Scanner ────────────────────────────────────────────────
function scanPageProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  try {
    // Strategy 1: Look for JSON-LD structured data
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' && data.offers) {
          const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          products.push({
            title: data.name || 'Producto',
            editionLabel: data.description || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${parseFloat(offer.price).toLocaleString('es-AR')}`,
            thumbnailUrl: data.image || null
          });
        }
      } catch {
        /* skip malformed JSON-LD */
      }
    });
    if (products.length > 0) return products;
    // Strategy 2: Scan for price patterns in CTA areas
    const priceRegex = /\$\s?([\d.,]+)/g;
    const ctaButtons = document.querySelectorAll('a[href*="checkout"], button, [class*="cta"], [class*="CTA"]');
    ctaButtons.forEach(btn => {
      const text = btn.textContent || '';
      const match = priceRegex.exec(text);
      priceRegex.lastIndex = 0;
      if (match) {
        const priceStr = match[1].replace(/\./g, '').replace(',', '.');
        const price = parseFloat(priceStr);
        if (price > 0) {
          // Find nearby image
          const section = btn.closest('section') || btn.parentElement;
          const img = section?.querySelector('img');
          products.push({
            title: 'Botiquín Ancestral',
            editionLabel: text.includes('ORDER BUMP') || text.includes('order bump') ? 'Pack Completo + Order Bump' : 'Pack Completo + Bonos',
            priceNumber: price,
            priceText: `$${price.toLocaleString('es-AR')}`,
            thumbnailUrl: img?.src || null
          });
        }
      }
    });
  } catch {
    /* scanner failed, will use fallbacks */
  }
  return products;
}
// ── Helpers ─────────────────────────────────────────────────────────
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickNameAvoidingRecent(recentNames: string[]): string {
  const available = NAMES.filter(n => !recentNames.includes(n));
  if (available.length === 0) return pickRandom(NAMES);
  return pickRandom(available);
}
function pickProduct(products: ScannedProduct[]): ScannedProduct {
  if (products.length === 0) {
    // Fallback distribution: 70% pack completo, 30% pack + order bump
    return Math.random() < 0.7 ? FALLBACK_PRODUCTS[0] : FALLBACK_PRODUCTS[1];
  }
  return pickRandom(products);
}
function randomInterval(): number {
  return Math.floor(Math.random() * 7000) + 8000; // 8–15 seconds
}
// ── Prefetch reduced motion preference ─────────────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}
// ── Component ──────────────────────────────────────────────────────
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const productsRef = useRef<ScannedProduct[]>([]);
  const recentNamesRef = useRef<string[]>([]);
  const idCounter = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reducedMotion = usePrefersReducedMotion();
  const showToast = useCallback(() => {
    const name = pickNameAvoidingRecent(recentNamesRef.current);
    recentNamesRef.current = [...recentNamesRef.current.slice(-9), name];
    const city = pickRandom(CITIES);
    const product = pickProduct(productsRef.current);
    const id = ++idCounter.current;
    setToast({
      id,
      name,
      city,
      product,
      visible: true
    });
    // Hide after 4 seconds
    setTimeout(() => {
      setToast(prev => prev && prev.id === id ? {
        ...prev,
        visible: false
      } : prev);
      // Remove from DOM after animation
      setTimeout(() => {
        setToast(prev => prev && prev.id === id ? null : prev);
      }, reducedMotion ? 50 : 400);
    }, 4000);
  }, [reducedMotion]);
  const scheduleNext = useCallback(() => {
    timerRef.current = setTimeout(() => {
      showToast();
      scheduleNext();
    }, randomInterval());
  }, [showToast]);
  useEffect(() => {
    // Scan products on mount
    const scanned = scanPageProducts();
    productsRef.current = scanned.length > 0 ? scanned : [];
    // First toast after 3 seconds
    const initialTimer = setTimeout(() => {
      showToast();
      scheduleNext();
    }, 3000);
    return () => {
      clearTimeout(initialTimer);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [showToast, scheduleNext]);
  if (!toast) return null;
  const {
    name,
    city,
    product,
    visible
  } = toast;
  const thumbnail = product.thumbnailUrl || FALLBACK_PRODUCTS[0].thumbnailUrl;
  return <div className="fixed bottom-4 left-1/2 z-50 pointer-events-none" style={{
    transform: 'translateX(-50%)'
  }} data-id="element-176">
      <a href={product.checkoutUrl || 'https://ebookpromax.com/cart/add?id=46629742641310&quantity=1'} target="_blank" rel="noopener noreferrer" className={`pointer-events-auto bg-white rounded-2xl shadow-2xl border border-brown/10 p-3 pr-5 flex items-center gap-3 max-w-sm w-[340px] cursor-pointer hover:shadow-3xl transition-shadow ${reducedMotion ? visible ? 'opacity-100' : 'opacity-0' : visible ? 'animate-toast-in' : 'animate-toast-out'}`} role="status" aria-live="polite" data-id="element-177">
        {/* Thumbnail */}
        {thumbnail && <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-sage" data-id="element-178">
            <img src={thumbnail} alt={product.title} className="w-full h-full object-cover" data-id="element-179" />
          </div>}

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-180">
          <p className="text-sm text-brown leading-snug" data-id="element-181">
            <span className="font-bold" data-id="element-182">{name}</span>
            <span className="text-brown/60" data-id="element-183"> en </span>
            <span className="font-semibold" data-id="element-184">{city}</span>
          </p>
          <p className="text-xs text-brown/70 mt-0.5 truncate" data-id="element-185">
            compró{' '}
            <span className="font-semibold text-forest" data-id="element-186">
              {product.editionLabel}
            </span>
          </p>
          <p className="text-xs font-bold text-forest mt-0.5" data-id="element-187">
            {product.priceText}
          </p>
        </div>

        {/* Time indicator */}
        <span className="text-[10px] text-brown/40 whitespace-nowrap self-start mt-1" data-id="element-188">
          hace {Math.floor(Math.random() * 5) + 1} min
        </span>
      </a>
    </div>;
}